<template>
  <div>
    <van-form validate-first scroll-to-error @submit="onSubmit">

      <DictSelect v-model="attendanceDetail.schoolId" name="school" label="上课校区" placeholder="请选择上课校区" required
        :rules="[{ required: true, message: '请选择上课校区' }]"
        :columns="schoolList" :column-names="{ text: 'schoolName', value: 'schoolId' }" @confirm="onSchoolConfirm" />

      <DictSelect v-model="attendanceDetail.classroomId" name="classroom" label="教室" placeholder="请选择教室（非必填）"
        :columns="classroomList" :column-names="{ text: 'classroomName', value: 'classroomId' }" @confirm="onClassroomConfirm" />


      <DictSelect v-model="attendanceDetail.periodsNumber" name="semester" label="期数" placeholder="请选择期数" required
        :rules="[{ required: true, message: '请选择期数' }]"
        :columns="semesterList" @confirm="onPeriodConfirm" />

      <DictSelect v-model="attendanceDetail.teachingMethod" name="teachingMethod" label="上课方式" placeholder="请选择上课方式" required
        :rules="[{ required: true, message: '请选择上课方式' }]"
        :columns="teachingMethodList" @confirm="onTeachingModeConfirm" />

      <van-field v-model="attendanceDetail.classDate" name="classDate" label="上课日期" placeholder="请选择上课日期" required
        :rules="[{ required: true, message: '请选择上课日期' }]" readonly clickable
        @click="showDatePicker = true" />
      <div v-if="attendanceDetail.classDates.length > 3" class="date-preview">
        {{ attendanceDetail.classDate }}
      </div>
      <van-calendar :show="showDatePicker" :default-date="[]" :min-date="calendarMinDate" :lazy-render="false"
        type="multiple" position="bottom" @confirm="onDateConfirm" @close="showDatePicker = false" />

      <van-field v-model="attendanceDetail.startTime" name="startTime" label="上课开始时间" placeholder="请选择开始时间" required
        :rules="[{ required: true, message: '请选择上课开始时间' }]" readonly clickable
        @click="showStartTimePicker = true; startTimeDefault = attendanceDetail.startTime.split(':') || ['12', '00']" />
      <van-popup v-model:show="showStartTimePicker" position="bottom">
        <van-time-picker v-if="showStartTimePicker" v-model="startTimeDefault" @confirm="onStartTimeConfirm"
          @cancel="showStartTimePicker = false" />
      </van-popup>

      <van-field v-model="attendanceDetail.endTime" name="endTime" label="上课结束时间" placeholder="请选择结束时间" required
        :rules="[{ required: true, message: '请选择上课结束时间' }]" readonly clickable
        @click="showEndTimePicker = true; endTimeDefault = attendanceDetail.endTime.split(':') || ['13', '00']" />
      <van-popup v-model:show="showEndTimePicker" position="bottom">
        <van-time-picker v-if="showEndTimePicker" v-model="endTimeDefault" @confirm="onEndTimeConfirm"
          @cancel="showEndTimePicker = false" />
      </van-popup>

      <DictSelectWithSearch v-model="attendanceDetail.teacherId" name="teacher" label="上课教师" placeholder="请选择上课教师" required
        :rules="[{ required: true, message: '请选择上课教师' }]"
        :columns="teacherList" :column-names="{ text: 'nickName', value: 'userId' }" @confirm="onTeacherConfirm" />

      <DictSelectWithSearch v-model="attendanceDetail.courseId" name="course" label="课程" placeholder="请选择课程" required
        :rules="[{ required: true, message: '请选择课程' }]"
        :columns="courseList" :column-names="{ text: 'courseName', value: 'courseId' }" @confirm="onCourseConfirm" />

      <van-field label="是否跨校区">
        <template #input>
          <van-switch v-model="attendanceDetail.isTransCampus" />
        </template>
      </van-field>

      <van-field v-model="attendanceDetail.overClassHour" label="加班课时" placeholder="请输入加班课时数" type="number" />

      <van-field v-model="attendanceDetail.studentNames" name="studentNames" label="上课学员" placeholder="请选择学员" required
        :rules="[{ required: true, message: '请选择上课学员' }]" readonly clickable
        @click="showStudent()">
        <template #button>
          <van-icon name="close" v-if="attendanceDetail.studentNames" @click.stop="clearStudents"
            style="font-size: 16px; color: #999;" />
        </template>
      </van-field>

      <van-popup v-model:show="showStudentPicker" position="bottom">
        <div style="height: 80vh; overflow-y: auto;">
          <van-search v-model="studentSearchQuery" placeholder="搜索学生姓名" show-action @update:model-value="filterStudents"
            @cancel="showStudentPicker = false" />

          <!-- 加载中提示 -->
          <div v-if="studentLoading" style="text-align: center; padding: 20px;">
            <van-loading type="spinner" />
            <p style="margin-top: 8px; color: #666;">加载学生列表中...</p>
          </div>

          <!-- 学生列表 -->
          <van-cell v-for="student in filteredStudentList" :key="student.studentId" :title="student.studentName"
            :disabled="student.isLocked === 1" @click="selectStudent(student)">
            <template #label>
              <van-tag v-if="student.isLocked === 1" type="danger" plain style="margin-top: 2px;">已锁定</van-tag>
            </template>
          </van-cell>
        </div>
      </van-popup>

      <div style="margin: 16px;">
        <van-button round block type="primary" native-type="submit">
          保存
        </van-button>
      </div>

      <!-- 加载遮罩 -->
      <van-overlay :show="loading">
        <div class="loading-wrapper">
          <van-loading size="24px">提交中...</van-loading>
        </div>
      </van-overlay>

      <!-- 排课冲突详情 -->
      <ScheduleConflictPopup v-model:show="conflictVisible" :message="conflictMessage" :data="conflictData" />
    </van-form>
  </div>
</template>


<script setup>
import { onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { message } from 'ant-design-vue'
import { getScheduleCourseList, getStudentListByCourseH5, addSchedule, getClassroomList } from '@/api/schedule'
import { getSchoolDicts } from '@/api/dict'
import { getTeacherList } from '@/api/user'
import { useDict } from '@/utils/dict'
import DictSelect from '@/components/DictSelect.vue'
import DictSelectWithSearch from '@/components/DictSelectWithSearch.vue'
import ScheduleConflictPopup from '@/components/ScheduleConflictPopup.vue'

const route = useRoute()
const router = useRouter()
const { teaching_method: teachingMethodList, semester_list: semesterList, loadDicts } = useDict('teaching_method', 'semester_list')

function createAttendanceDetail() {
  return { startTime: '', endTime: '', isTransCampus: false, overClassHour: 0, classDate: '', classDates: [], studentList: [], studentNames: '', schoolName: '', semesterName: '', teachingMethodName: '' }
}

const loading = ref(false)
const courseId = ref(null)
const studentLoading = ref(false)
const calendarMinDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
const attendanceDetail = reactive(createAttendanceDetail())
const conflictVisible = ref(false)
const conflictData = ref([])
const conflictMessage = ref('')


const studentSearchQuery = ref('')
const showDatePicker = ref(false)
const showStartTimePicker = ref(false)
const showEndTimePicker = ref(false)
const showStudentPicker = ref(false)
const teacherList = ref([])
const studentList = ref([])
const filteredStudentList = ref([])
const courseList = ref([])
const startTimeDefault = ref(['12', '00'])
const endTimeDefault = ref(['13', '00'])
const schoolList = ref([])
const classroomList = ref([])

onMounted(() => {
  Object.assign(attendanceDetail, createAttendanceDetail())
  courseId.value = route.query.courseId
  getCourseList()
  getTeachers()
  getOtherDicts()
})

function getOtherDicts() {
  loadDicts()
  getSchoolDicts().then(res => {
    schoolList.value = res
    if (res && res.length === 1) {
      const school = res[0]
      attendanceDetail.schoolName = school.schoolName
      attendanceDetail.schoolId = school.schoolId
      loadClassrooms(school.schoolId)
    }
  })
}

function showStudent() {
  if (!attendanceDetail.courseId || !attendanceDetail.startTime || !attendanceDetail.endTime) {
    message.warning('请先选择课程及上课时间')
    return
  }
  const params = { courseId: attendanceDetail.courseId, teachingModel: attendanceDetail.teachingModel, grade: attendanceDetail.grade, subject: attendanceDetail.subject, startTime: attendanceDetail.startTime, endTime: attendanceDetail.endTime }
  studentLoading.value = true
  showStudentPicker.value = true
  getStudentListByCourseH5(params).then(response => {
    studentList.value = response
    filteredStudentList.value = response
  }).finally(() => {
    studentLoading.value = false
  })
}

function selectStudent(student) {
  if (student.isLocked === 1) {
    message.warning('该学员已锁定，需解锁审核通过后才能排课')
    return
  }
  attendanceDetail.studentList.push(student)
  attendanceDetail.studentNames = attendanceDetail.studentList.map(student => student.studentName).join(', ')
  showStudentPicker.value = false
}

function filterStudents() {
  const query = studentSearchQuery.value
  filteredStudentList.value = studentList.value.filter(student => student.studentName.includes(query))
}

function onDateConfirm(selectedDates) {
  const dates = Array.isArray(selectedDates) ? selectedDates : [selectedDates]
  const formattedDates = dates.filter(Boolean).map(date => formatDate(date)).sort()
  attendanceDetail.classDates = formattedDates
  attendanceDetail.classDate = formattedDates.join('、')
  showDatePicker.value = false
}

function formatDate(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function clearStudents() {
  attendanceDetail.studentNames = ''
  attendanceDetail.studentList = []
}

function getCourseList() {
  getScheduleCourseList({}).then(response => {
    courseList.value = response
  })
}

function getTeachers() {
  getTeacherList().then(response => {
    teacherList.value = response
    if (response && response.length === 1) {
      const teacher = response[0]
      attendanceDetail.teacherName = teacher.nickName
      attendanceDetail.teacherId = teacher.userId
    }
  })
}

function onStartTimeConfirm(time) {
  attendanceDetail.startTime = time.selectedValues[0] + ':' + time.selectedValues[1]
  showStartTimePicker.value = false
}

function onEndTimeConfirm(time) {
  attendanceDetail.endTime = time.selectedValues[0] + ':' + time.selectedValues[1]
  showEndTimePicker.value = false
}

function onSchoolConfirm({ value, label }) {
  attendanceDetail.schoolId = value
  attendanceDetail.schoolName = label
  attendanceDetail.classroomId = undefined
  attendanceDetail.classroomName = ''
  loadClassrooms(value)
}

function loadClassrooms(schoolId) {
  if (!schoolId) {
    classroomList.value = []
    return
  }
  getClassroomList({ schoolId, status: '1' }).then(response => {
    classroomList.value = response || []
  })
}

function onClassroomConfirm({ value, label }) {
  attendanceDetail.classroomId = value
  attendanceDetail.classroomName = label
}

function onTeacherConfirm({ value, label }) {
  attendanceDetail.teacherId = value
  attendanceDetail.teacherName = label
}



function onCourseConfirm({ value, selected }) {
  courseId.value = value
  attendanceDetail.courseId = value
  attendanceDetail.courseName = selected.courseName
  attendanceDetail.subject = selected.subject
  attendanceDetail.grade = selected.grade
  attendanceDetail.teachingModel = selected.teachingModel
  attendanceDetail.studentNames = ''
  attendanceDetail.studentList = []
}

function convertTimeToMinutes(timeStr) {
  if (!timeStr) return 0
  const [hours, minutes] = timeStr.split(':').map(Number)
  return hours * 60 + (minutes || 0)
}

function onPeriodConfirm({ value, label }) {
  attendanceDetail.periodsNumber = value
  attendanceDetail.semesterName = label
}

function onTeachingModeConfirm({ value, label }) {
  attendanceDetail.teachingMethod = value
  attendanceDetail.teachingMethodName = label
}

function onSubmit() {
  const startMinutes = convertTimeToMinutes(attendanceDetail.startTime)
  const endMinutes = convertTimeToMinutes(attendanceDetail.endTime)
  if (endMinutes <= startMinutes) {
    message.error('上课结束时间必须大于开始时间')
    return
  }
  loading.value = true
  const param = { teachingModel: attendanceDetail.teachingModel, subject: attendanceDetail.subject, grade: attendanceDetail.grade, courseName: attendanceDetail.courseName, schoolId: attendanceDetail.schoolId, classroomId: attendanceDetail.classroomId, periodsNumber: attendanceDetail.periodsNumber, teachingMethod: attendanceDetail.teachingMethod, classDates: attendanceDetail.classDates, classTimes: [attendanceDetail.startTime, attendanceDetail.endTime], teacherId: attendanceDetail.teacherId, courseId: courseId.value, isTransCampus: attendanceDetail.isTransCampus ? 1 : 0, overClassHour: attendanceDetail.overClassHour, studentList: attendanceDetail.studentList }
  addSchedule(param).then(() => {
    message.success('保存成功！')
    router.back()
  }).catch(err => {
    if (err && err.data && Array.isArray(err.data) && err.data.length > 0) {
      conflictData.value = err.data
      conflictMessage.value = err.msg || '存在排课时间冲突，冲突详情如下：'
      conflictVisible.value = true
    }
  }).finally(() => {
    loading.value = false
  })
}
</script>

<style scoped>
/* 弹窗内容区域 */
.van-popup--bottom {
  background-color: #fff;
}

/* 搜索框样式 */
.van-search {
  padding: 10px 16px;
}

/* 列表项样式 */
.van-cell {
  border-bottom: 1px solid #ebedf0;
}

/* 读取字段样式 */
.readonly-field {
  background-color: #f7f8fa;
  cursor: not-allowed;
}

.loading-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.date-preview {
  margin: -6px 16px 10px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f8fafc;
  color: #64748b;
  font-size: 13px;
  word-break: break-all;
}
</style>
